import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { deleteSocialMediaConfig, facebookAuthConfig, getSocialMedia, toggleSocialMedia, twitchConfigure, youtubeAuthConfig } from "../../services/socialmedia.service";
import { ErrorMessage, Field, Form, Formik } from "formik";
import Swal from "sweetalert2";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import * as Yup from "yup";
import moment from 'moment';
import { AlertCircle, Trash2 } from "lucide-react";

function ManageSocialMedia() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showMonetization, setShowMonetization] = useState({});
  const myGlobalData = useSelector((state) => state.globalData);
  const [globalData, setGlobalData] = useState(() => {
    if (myGlobalData?.data) {
      try {
        return typeof myGlobalData.data === "string" ? JSON.parse(myGlobalData.data) : myGlobalData.data;
      } catch (e) {
        return {};
      }
    }
    return {};
  });

  useEffect(() => {
    if (myGlobalData && myGlobalData?.data) {
      try {
        const settingsData = typeof myGlobalData.data === "string" ? JSON.parse(myGlobalData.data) : myGlobalData.data;
        setGlobalData(settingsData);
      } catch (e) {
        console.error("Error parsing globalData in ManageSocialMedia:", e);
      }
    }
  }, [myGlobalData]);

  useEffect(() => {
    fetchSocialMedia();

    // Re-fetch when user returns from social auth popup
    const handleFocus = () => {
      fetchSocialMedia();
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);

  const fetchSocialMedia = async () => {
    try {
      const res = await getSocialMedia({});
      if (res?.success) {
        setData(res.data);
        // Auto-revert logic: if enabled but 0 config, turn off
        const platformsToCheck = ["facebook", "youtube", "twitch"];
        platformsToCheck.forEach(async (p) => {
          if (res.data[p]?.enabled && (!res.data[p].config || res.data[p].config.length === 0)) {
            await toggleSocialMedia({ platform: p }); // Toggle OFF
          }
        });
      }
    } catch (error) {
      console.log("CMS Fetch Error:", error);
    }
    setLoading(false);
  };

  const icons = {
    facebook: "/images/f01.png",
    youtube: "/images/f02.png",
    twitch: "/images/twitch.png",
    instagram: "/images/f03.png",
  };

  const isPlatformEnabled = (platform) => {
    if (platform === "facebook") return String(globalData?.show_facebook_stream) === "true";
    if (platform === "youtube") return String(globalData?.show_youtube_stream) === "true";
    if (platform === "twitch") return String(globalData?.show_twitch_stream) === "true";
    return false;
  };

  const platforms = ["facebook", "youtube", "twitch"].filter(isPlatformEnabled);

  if (loading) {
    return <div className="py-20 text-center text-gray-400 body-lg">{t('advertiser.invoice.loading')}</div>;
  }

  // Validation for Twitch
  const validationSchema = Yup.object().shape({
    accountName: Yup.string().required("Account name is required"),
    twitchKey: Yup.string().required("Stream Key is required"),
    twitchUrl: Yup.string().required("Twitch URL is required"),
  });

  // API submission
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const payload = {
        enabled: true,
        config: {
          accountName: values.accountName,
          twitchKey: values.twitchKey,
          twitchUrl: values.twitchUrl,
        },
      };

      const response = await twitchConfigure(payload);
      resetForm();
      if (response.success) {
        Swal.fire({ icon: "success", text: response.message, ...SWAL_SETTINGS });
        // Auto-enable if it's the first config
        if (!data.twitch?.enabled) {
          await proceedToggle("twitch");
        }
      } else {
        Swal.fire({
          icon: "error",
          text: response.message,
          ...SWAL_SETTINGS,
        });
      }
    } catch (error) {
      console.log(error);
      Swal.fire({
        icon: "error",
        text: "Something went wrong!",
        ...SWAL_SETTINGS,
      });
    } finally {
      setSubmitting(false);
    }
  };


  const handleToggle = async (platform, isTurningOn) => {
    // If turning ON but no config, trigger auth/config flow instead
    if (isTurningOn && (!data[platform]?.config || data[platform].config.length === 0)) {
      if (platform === "facebook") {
        handleFacebookAuth(null, true);
      } else if (platform === "youtube") {
        handleYoutubeAuth(null, true);
      } else if (platform === "twitch") {
        // For twitch, we just let it enable so they can see the form
        // or we can auto-focus the form.
        await proceedToggle(platform);
      }
      return;
    }

    await proceedToggle(platform);
  };

  const proceedToggle = async (platform) => {
    try {
      const payload = {
        platform: platform,
      };
      const response = await toggleSocialMedia(payload);
      if (response.success) {
        Swal.fire({ icon: "success", text: response.message, ...SWAL_SETTINGS });
      } else {
        Swal.fire({
          icon: "error",
          text: response.message,
          ...SWAL_SETTINGS,
        });
      }
      await fetchSocialMedia();
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to toggle platform");
    }
  };

  const handleFacebookAuth = async (existingId = null, autoToggle = false) => {
    try {
      const res = await facebookAuthConfig();

      if (res?.success && res?.data?.url) {
        if (autoToggle) {
          // If we are auto-toggling, we should probably tell the user
          // But usually window.open is enough.
        }
        window.open(res.data.url, "_blank");
      } else {
        Swal.fire({
          icon: "error",
          text: res?.message || "Auth URL not received",
          ...SWAL_SETTINGS,
        });
      }
    } catch (err) {
      console.log(err);
      Swal.fire({ icon: "error", text: "Something went wrong", ...SWAL_SETTINGS });
    }
  };


  const handleYoutubeAuth = async (mediaId, autoToggle = false) => {
    try {
      const payload = {
        mediaId: mediaId,
      };
      const res = await youtubeAuthConfig(payload);
      if (res?.success && res?.data?.url) {
        window.open(res.data.url, "_blank");
      } else {
        Swal.fire({
          icon: "error",
          text: res?.message || "Auth URL not received",
          ...SWAL_SETTINGS,
        });
      }
    } catch (err) {
      console.log(err);
      Swal.fire({ icon: "error", text: "Something went wrong", ...SWAL_SETTINGS });
    }
  };

  const handleDeleteConfig = async (platform, configId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to remove this saved account?",
      icon: "warning",
      showCancelButton: true,
      showConfirmButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        const response = await deleteSocialMediaConfig({ platform, configId });
        if (response?.success) {
          Swal.fire({ icon: "success", text: response.message || "Account removed successfully", ...SWAL_SETTINGS });
          fetchSocialMedia();
        } else {
          Swal.fire({
            icon: "error",
            text: response?.message || "Failed to remove account",
            ...SWAL_SETTINGS,
          });
        }
      } catch (err) {
        console.error(err);
        Swal.fire({ icon: "error", text: "Something went wrong!", ...SWAL_SETTINGS });
      }
    }
  };

  return (
    <div className="w-full space-y-[15px]">
      {platforms.length === 0 ? (
        <div className="p-8 text-center text-gray-500 bg-white rounded-[10px] border border-zinc-100">
          No social media platforms are currently enabled.
        </div>
      ) : (
        platforms.map((platform) => (
          <div
            key={platform}
            className="bg-white flex flex-col justify-between flex-wrap gap-[10px] p-[15px] rounded-[10px] shadow-[0px_2px_24px_0px_rgba(0,0,0,0.05)] border-[1px] !border-zinc-100"
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-[10px] flex-wrap w-full">
              <div className="flex items-center gap-[10px]">
                <img
                  src={icons[platform]}
                  alt={platform}
                  className={platform === "twitch" ? "w-[33px]" : ""}
                />
                <span className="text-[15px] semibold text-gray-950 capitalize">
                  {platform}
                </span>

                <div
                  className="flex items-center gap-1 cursor-pointer bg-gray-50 px-2 py-1 rounded-md hover:bg-gray-100 transition-colors ml-2"
                  onClick={() => {
                    setShowMonetization(prev => ({
                      ...prev,
                      [platform]: !prev[platform]
                    }));
                  }}
                >
                  <span className="text-[11px] text-gray-500 font-medium">{t('advertiser.profile.monetization_policy')}</span>
                  <AlertCircle className={`w-3.5 h-3.5 transition-colors ${showMonetization[platform] ? "text-indigo-600" : "text-gray-400"}`} />
                </div>
              </div>

              <input
                type="checkbox"
                checked={data?.[platform]?.enabled || false}
                onChange={(e) => handleToggle(platform, e.target.checked)}
                className="toggle border-indigo-600 bg-indigo-500 checked:border-orange-500 checked:bg-orange-400 checked:text-orange-800"
              />
            </div>

          {showMonetization[platform] && (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-[11px] text-gray-600 leading-relaxed mb-2 animate__animated animate__fadeIn">
              {t('advertiser.profile.monetization_desc')}
            </div>
          )}
          {platform === "facebook" && data.facebook?.enabled && (
            <>
              <div className="p-2 rounded-lg ">
                <button
                  onClick={handleFacebookAuth}
                  className="btn btn-pri"
                >
                  {t('advertiser.profile.configure')}
                </button>
              </div>
              {data.facebook.config.length > 0 && (
                <div className="space-y-3 mt-3">

                  {data.facebook.config.map((acc, index) => {
                    const expiryTimestamp = acc?.userTokenData?.data_access_expires_at;
                    const expiryDate = expiryTimestamp
                      ? moment.unix(expiryTimestamp)
                      : null;
                    const isExpired = expiryDate ? moment().isAfter(expiryDate) : true;


                    return (
                      <div
                        key={acc._id || index}
                        className="p-3 border rounded-lg bg-gray-50 flex items-start justify-between"
                      >
                        <div className="flex flex-col">
                          <p className="font-semibold text-gray-800">
                            {acc.pageName}
                          </p>

                          <p className="text-sm text-gray-600">
                            {t('advertiser.profile.token_expiry')} {expiryDate ? expiryDate.format("DD-MM-YYYY") : "—"}
                          </p>
                          {isExpired &&

                            <button
                              onClick={() => handleFacebookAuth(acc._id)}
                              className="btn btn-pri mt-2 w-fit"
                            >
                              {t('advertiser.profile.reauthenticate')}
                            </button>
                          }
                        </div>

                        <div className="flex items-center gap-2">
                          <button className="btn btn-success">
                            {isExpired ? t('advertiser.profile.expired') : t('advertiser.profile.active')}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteConfig("facebook", acc._id)}
                            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-200 cursor-pointer"
                            title="Delete Account"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

            </>
          )}
          {platform === "youtube" && data.youtube?.enabled && (
            <>
              <div className="p-2 rounded-lg ">
                <button
                  onClick={() => handleYoutubeAuth(null)}
                  className="btn btn-pri"
                >
                  {t('advertiser.profile.configure')}
                </button>
              </div>

              {data.youtube.config.length > 0 && (
                <div className="space-y-3 mt-3">

                  {data.youtube.config.map((acc, index) => {
                    const updatedAt = acc.updatedAt;
                    const expiryDate = acc.tokenData?.expiry_date ? moment(acc.tokenData.expiry_date) : null;
                    const isExpired = !acc.tokenData?.access_token;



                    return (
                      <div
                        key={acc._id || index}
                        className="p-3 border rounded-lg bg-gray-50 flex items-start justify-between"
                      >
                        <div className="flex flex-col">
                          <p className="font-semibold text-gray-800">
                            {acc.accountName}
                          </p>

                          <p className="text-sm text-gray-600">
                            {t('advertiser.profile.token_expiry')} {expiryDate ? expiryDate.format("DD-MM-YYYY") : "—"}
                          </p>
                          {isExpired &&

                            <button
                              onClick={() => handleYoutubeAuth(acc._id)}
                              className="btn btn-pri mt-2 w-fit"
                            >
                              {t('advertiser.profile.reauthenticate')}
                            </button>
                          }
                        </div>

                        <div className="flex items-center gap-2">
                          <button className="btn btn-success">
                            {isExpired ? t('advertiser.profile.expired') : t('advertiser.profile.active')}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteConfig("youtube", acc._id)}
                            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-200 cursor-pointer"
                            title="Delete Account"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

            </>
          )}

          {/* Show form only for Twitch */}
          {platform === "twitch" && data.twitch?.enabled && (
            <>
              <Formik
                enableReinitialize
                initialValues={{
                  accountName: "",
                  twitchKey: "",
                  twitchUrl: "rtmp://live.twitch.tv/app",
                }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting }) => (
                  <Form>
                    <div className="edit-profile-form-bg mt-3">
                      <div className="form-group">
                        <label>{t('advertiser.profile.account_name')}</label>
                        <Field type="text" name="accountName" className="form-control-custom" placeholder={t('advertiser.profile.account_name')} />
                        <div className="text-sm text-red-600 mt-1">
                          <ErrorMessage name="accountName" />
                        </div>
                      </div>

                      <div className="form-group">
                        <label>{t('advertiser.profile.stream_key')}</label>
                        <Field type="text" name="twitchKey" className="form-control-custom" placeholder={t('advertiser.profile.stream_key')} />
                        <div className="text-sm text-red-600 mt-1">
                          <ErrorMessage name="twitchKey" />
                        </div>
                      </div>

                      <div className="form-group">
                        <label>{t('advertiser.profile.twitch_url')}</label>
                        <Field type="text" name="twitchUrl" className="form-control-custom" placeholder={t('advertiser.profile.twitch_url')} />
                        <div className="text-sm text-red-600 mt-1">
                          <ErrorMessage name="twitchUrl" />
                        </div>
                      </div>

                      <button type="submit" className="btn btn-pri min-w-[181px]" disabled={isSubmitting}>
                        {isSubmitting ? t('advertiser.profile.updating') : t('advertiser.profile.save')}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
              <div>
                {data.twitch.config.length > 0 && (
                  <div className="space-y-3 mt-3">

                    {data.twitch.config.map((acc, index) => {

                      return (
                        <div
                          key={acc._id || index}
                          className="p-3 border rounded-lg bg-gray-50 flex items-start justify-between gap-3"
                        >
                          <div className="flex flex-col">
                            <p className="font-semibold text-gray-800">
                              {t('advertiser.profile.account_name')}: {acc.accountName}
                            </p>

                            {/* <p className="font-semibold text-gray-800">
                              Twitch Key: {acc.twitchKey}
                            </p> */}

                            <p className="font-semibold text-gray-800">
                              {t('advertiser.profile.twitch_url')}: {acc.twitchUrl}
                            </p>
                          </div>

                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleDeleteConfig("twitch", acc._id)}
                              className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-200 cursor-pointer"
                              title="Delete Account"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
          {platform === "instagram" && (
            <>
              <Formik
                enableReinitialize
                initialValues={{
                  accountName: "",
                  instagramKey: "",
                  instagramUrl: "",
                }}
                validationSchema={validationSchema}
                // onSubmit={handleSubmit}
              >
                {({ isSubmitting }) => (
                  <Form>
                    <div className="edit-profile-form-bg mt-1">
                      {/* <div className="form-group">
                        <label>Account Name</label>
                        <Field type="text" name="accountName" className="form-control-custom" placeholder="Enter account name" />
                        <div className="text-sm text-red-600 mt-1">
                          <ErrorMessage name="accountName" />
                        </div>
                      </div> */}

                      {/* As of now, Instagram does not provide a 3rd party API. */}

                      {/* <button type="submit" className="btn btn-pri min-w-[181px]" disabled={isSubmitting}>
                        {isSubmitting ? "Saving..." : "Save"}
                      </button> */}
                    </div>
                  </Form>
                )}
              </Formik>
              <div>
          
              </div>
            </>
          )}

        </div>
      ))
    )}
  </div>
);
}

export default ManageSocialMedia;
